import React, { useState } from 'react';
import { Box, Button, Typography, Grid, Card, CardMedia, CardContent,
  IconButton, TextField, Select, InputLabel, FormControl, MenuItem, Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { Document, Page, pdfjs } from 'react-pdf';
import { useDocumentTypeQuery } from '../../../../queries/useDocumentTypeQuery';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;


const Input = styled('input')({ display: 'none', });

export interface PreviewFile {
  file: File;
  documentType: string;
  previewUrl: string;
  description: string;
}

interface Props {
  files?:PreviewFile[];
  clearOnUpload?: boolean;
  usePublicAPI:boolean;
  onUpload?: (files: PreviewFile[]) => Promise<void>|void;
  onUpdate?: (files: PreviewFile[]) => void;
}

const MAX_FILES = 8;

const FileUploadPreview: React.FC<Props> = ({files:initialFiles, onUpload, onUpdate, usePublicAPI, clearOnUpload = true}) => {
  const [files, setFiles] = useState<PreviewFile[]>(initialFiles||[]);

  React.useEffect(() => { onUpdate && onUpdate(files); },[files, onUpdate])

  const documentTypes = useDocumentTypeQuery(usePublicAPI);
  const documentMenuItems = React.useMemo(() => {
    if(!documentTypes.data) {
      return []
    }
    const entries = Object.entries(documentTypes.data)?.sort();
    return entries?.map((e) => <MenuItem key={e[0]} value={e[0]}>{e[1]}</MenuItem>) || []
  }, [documentTypes]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const remainingSlots = MAX_FILES - files.length;

    const filesToAdd = selectedFiles.slice(0, remainingSlots).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      documentType: '',
      description: '',
    }));

    setFiles((prev) => [...prev, ...filesToAdd]);
    event.target.value = '';
  };

  const handleDescriptionChange = (index: number, newDesc: string) => {
    const updatedFiles = [...files];
    updatedFiles[index].description = newDesc;
    setFiles(updatedFiles);
  };

  const handleTypeChange = (file:PreviewFile, newType: string) => {
    setFiles((prev) => prev.map((f) => file.previewUrl == f.previewUrl ? {...f, documentType:newType} : f));
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...files];
    URL.revokeObjectURL(updatedFiles[index].previewUrl);
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  const handleUpload = async () => {
    if(onUpload) {
      await onUpload(files);
      if(clearOnUpload) {
        setFiles([]);
      }
    }
  };

  return (
    <Box p={2}>
      <Typography variant='h6' gutterBottom>
        Upload Files
      </Typography>

      <label htmlFor='upload-button'>
        <Input
          accept='image/*,application/pdf'
          id='upload-button'
          multiple
          type='file'
          onChange={handleFileChange}
          disabled={files.length >= MAX_FILES}
        />
        <Button
          variant='contained'
          color='primary'
          component='span'
          startIcon={<CloudUploadIcon />}
          disabled={files.length >= MAX_FILES}
        >
          Choose Files ({files.length}/{MAX_FILES})
        </Button>
      </label>

      {files.length > 0 && (
        <>
          <Box mt={3} paddingBottom={'14px'}>
            <Box
              sx={{
                maxHeight: 600, // Limit to approx. 2 rows depending on card height
                overflowY: 'auto',
                pr: 1,
              }}
            >
              <Grid container spacing={2}>
                {files.map((previewFile, index) => (
                  <Grid item xs={12} sm={6} md={4} pb={'5px'} key={index}>
                    <Card>
                      {previewFile.file.type.startsWith('image/') ? (
                        <CardMedia
                          component='img'
                          height='140'
                          image={previewFile.previewUrl}
                          alt={previewFile.file.name}
                        />
                      ) : previewFile.file.type === 'application/pdf' ? (
                        <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
                        <Document
                            file={previewFile.previewUrl}
                            loading="Loading Preview"
                            error="Failed to load PDF."
                          >
                            <Page pageNumber={1} width={250} height={140}
                              renderAnnotationLayer={false}
                              renderTextLayer={false}
                            />
                          </Document>
                          </div>
                      ) : (
                          <Box
                              title={previewFile.file.name}
                              height={140}
                              width='96%'
                              style={{ border: 'none' }}
                            >
                            <Typography>{previewFile.file.name}</Typography>
                          </Box>
                      )}

                      <CardContent>
                        <Typography variant='subtitle1'>{previewFile.file.name}</Typography>
                        <Stack direction='column' spacing={1.5} alignItems='center'>
                          <TextField
                            fullWidth
                            label='Document Details' variant='outlined' size='small'
                            value={previewFile.description}
                            onChange={(e) => handleDescriptionChange(index, e.target.value)}
                            InputLabelProps={{shrink:!!previewFile.description}}
                          />

                          <FormControl fullWidth size='small' variant='outlined'>
                            <InputLabel id="doc-type">Document Type</InputLabel>
                              <Select
                                id='doc-type'
                                value={previewFile.documentType}
                                label="Document Type"
                                error={!previewFile.documentType || previewFile.documentType == ''}
                                onChange={(e) => handleTypeChange(previewFile, e.target.value)}
                              >
                              {documentMenuItems}
                            </Select>
                          </FormControl>

                          <IconButton onClick={() => handleRemoveFile(index)}  >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>

        {
          onUpload ?
          <Box mt={2}>
            <Button
              disabled={!!files?.find((pv) => !pv.documentType || pv.documentType == '')}
              variant='contained'
              color='success'
              fullWidth
              onClick={handleUpload}
            >
              Upload Documents
            </Button>
          </Box>: <></>
        }
        </>
      )}
    </Box>
  );
};

export default FileUploadPreview;
